import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl } from "../lib/api";
import { Mail, Phone, Trash2, Calendar, User, ShieldCheck, FileText, ExternalLink, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CONFIGS = {
  insurance: {
    title: "Richieste Assicurazione",
    endpoint: "/insurance",
    icon: ShieldCheck,
    statuses: { new: "Nuova", contacted: "Contattato", closed: "Chiusa" },
    statusStyles: {
      new: "bg-orange-500/20 text-orange-300 border-orange-500/30",
      contacted: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      closed: "bg-white/10 text-white/50 border-white/10",
    },
    showPaymentPlan: true,
  },
  demolition: {
    title: "Richieste Demolizione",
    endpoint: "/demolition",
    icon: Wrench,
    statuses: { new: "Nuova", scheduled: "Appuntamento fissato", completed: "Completata" },
    statusStyles: {
      new: "bg-orange-500/20 text-orange-300 border-orange-500/30",
      scheduled: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    },
    showPaymentPlan: false,
  },
};

const PAYMENT_LABELS = { mensile: "Mensile", semestrale: "Semestrale", annuale: "Annuale" };

function DocLink({ label, path, type }) {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    if (!url) {
      try {
        const r = await api.get(`/images/${path}`, { responseType: "blob" });
        setUrl(URL.createObjectURL(r.data));
      } catch { toast.error("Errore caricamento documento"); return; }
    }
    setOpen(true);
  };
  if (!path) return <p className="text-white/40 italic text-xs">Non disponibile</p>;
  const isPdf = type === "application/pdf";
  return (
    <>
      <button onClick={load} className="text-gold hover:underline text-xs flex items-center gap-1"><ExternalLink size={12} /> Apri {label}</button>
      {open && url && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {isPdf ? <iframe src={url} className="w-full h-[80vh] bg-white" title={label} />
                   : <img src={url} alt={label} className="w-full max-h-[90vh] object-contain" />}
            <button onClick={() => setOpen(false)} className="mt-4 mx-auto block btn-gold px-6 py-2 text-xs uppercase tracking-widest font-semibold">Chiudi</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminDocumentRequests({ kind }) {
  const cfg = CONFIGS[kind];
  const Icon = cfg.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await api.get(cfg.endpoint); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [kind]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`${cfg.endpoint}/${id}`, { status }); toast.success("Stato aggiornato"); fetchAll(); }
    catch { toast.error("Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await api.delete(`${cfg.endpoint}/${id}`); toast.success("Eliminata"); fetchAll(); }
    catch { toast.error("Errore"); }
  };

  return (
    <AdminShell title={cfg.title}>
      {loading ? <div className="p-10 text-center text-white/50">Caricamento...</div>
      : items.length === 0 ? (
        <div className="bg-midnight-light border border-white/5 p-16 text-center">
          <Icon size={36} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/60">Nessuna richiesta ricevuta</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid={`${kind}-list`}>
          {items.map((r) => (
            <div key={r.id} className="bg-midnight-light border border-white/5 p-6" data-testid={`${kind}-item-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/20 flex items-center justify-center shrink-0">
                    <User size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{r.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-white/60">
                      <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-gold"><Phone size={11} /> {r.phone}</a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-gold"><Mail size={11} /> {r.email}</a>
                      <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(r.created_at).toLocaleString("it-IT")}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                    <SelectTrigger className={`w-52 rounded-sm border ${cfg.statusStyles[r.status]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(cfg.statuses).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => remove(r.id)} className="p-2 text-destructive hover:bg-destructive hover:text-white rounded-sm"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-white/5 text-xs">
                {cfg.showPaymentPlan && r.payment_plan && (
                  <div>
                    <p className="text-white/40 uppercase tracking-widest mb-1">Piano pagamento</p>
                    <p className="font-medium text-gold">{PAYMENT_LABELS[r.payment_plan]}</p>
                  </div>
                )}
                {kind === "demolition" && (
                  <>
                    <div>
                      <p className="text-white/40 uppercase tracking-widest mb-1">Auto marciante</p>
                      <p className={`font-medium ${r.is_running ? "text-emerald-400" : "text-orange-400"}`}>{r.is_running ? "Sì" : "No"}</p>
                    </div>
                    <div>
                      <p className="text-white/40 uppercase tracking-widest mb-1">Carroattrezzi</p>
                      <p className={`font-medium ${r.needs_tow ? "text-orange-400" : "text-white/70"}`}>{r.needs_tow ? "Richiesto" : "Non serve"}</p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Carta di circolazione</p>
                  <DocLink label="carta" path={r.circulation_doc_path} type={r.circulation_doc_content_type} />
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Documento identità</p>
                  <DocLink label="ID" path={r.id_doc_path} type={r.id_doc_content_type} />
                </div>
              </div>
              {r.notes && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-white/40 uppercase tracking-widest text-xs mb-2">Note</p>
                  <p className="text-sm text-white/80 whitespace-pre-line">{r.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
